import React, { useEffect, useCallback, useMemo, useState } from 'react';
import Traductor from '../Traductor';
import {
    filterAgrupations,
    filterBrands,
    filterModels,
    filterKm,
    filterFavorite,
    filterBidden,
    filterPlate,
    filterBatch,
    filterStates,
    filterBuyer,
    filterStadisticsBuyers,
    filterDatePurchaseFrom,
    filterDatePurchaseTo,
    filterComercialProducts,
    filterDealers,
    filterVIN,
    filterDatePurchaseOptionFrom,
    filterDatePurchaseOptionTo,
    filterFinancialOperation,
    filterTransportState,
} from '../../constants/Filters';
import { checkFilterAvailable } from '../../js/filterFunctions';
import { withCookies } from 'react-cookie';
import ParseService from '../../services/ParseService';
import BrandFilter from './BrandFilter';
import ListFilter from './ListFilter';
import RangeFilter from './RangeFilter';
import ButtonGroupFilter from './ButtonGroupFilter';
import VehicleService from '../../services/VehicleService';
import Globals from '../../Globals';

import InputFilter from './InputFilter';
import DatesFilter from './BuyDatesFilters';
import Button from 'components/bronson/Button';

import '../../css/components/vehicleFilters/vehicleFilters.css';

const VehicleFilters = ({
    cookies,
    refreshFilters,
    setInitialFilters,
    updateFilters,
    filters,
    filtersOptions,
    availableFilters,
}) => {
    const t = useMemo(() => new Traductor(), []);
    const [containerType] = useState('advanced');
    const [temporaryFilters, setTemporaryFilters] = useState(filters);

    useEffect(() => {
        setTemporaryFilters(filters);
    }, [filters]);

    const kmRangeFilterOptions = useMemo(
        () =>
            filtersOptions.KmsMax
                ? {
                      id: 'kmRangeFilter',
                      step: 1000,
                      pips: 50000,
                      range: [
                          filtersOptions.KmsMax[0],
                          filtersOptions.KmsMax[1],
                      ],
                      labelClass: '',
                      type: ['small'],
                      tooltips: false,
                      customValues: true,
                      isDate: false,
                  }
                : null,
        [filtersOptions.KmsMax]
    );

    const checkFilterAvailableCall = useCallback(
        filterName => {
            return checkFilterAvailable(availableFilters, filterName);
        },
        [availableFilters]
    );

    const selectFilter = useCallback(
        (name, code, isMultiSelect) => {
            const filterValue = temporaryFilters[name];
            let newFilerValue;

            if (filterValue) {
                if (isMultiSelect) {
                    if (filterValue.indexOf(code) > -1) {
                        newFilerValue = filterValue.filter(e => e !== code);
                    } else {
                        newFilerValue = [...filterValue, code];
                    }
                } else {
                    if (filterValue.indexOf(code) > -1) {
                        newFilerValue = [];
                    } else {
                        newFilerValue = [code];
                    }
                }
                let newFilters = {
                    ...temporaryFilters,
                    [name]: newFilerValue,
                };
                if (name === filterBrands) {
                    if (!newFilerValue.length) {
                        newFilters = {
                            ...newFilters,
                            filterModels: [],
                        };
                        setInitialFilters({ options: { Models: [] } });
                    }
                }
                setTemporaryFilters(newFilters);
            }
        },
        [temporaryFilters, setInitialFilters]
    );

    const printBrandFilter = useCallback(
        () => (
            <BrandFilter
                filterTitle={t.getTraduction('Brands')}
                itemList={filtersOptions.Brands}
                filterName={filterBrands}
                filters={temporaryFilters}
                selectFilter={selectFilter}
                showImages={true}
                isMultiSelect={true}
            />
        ),
        [t, filtersOptions.Brands, temporaryFilters, selectFilter]
    );

    const getModels = useCallback(
        brands => {
            if (brands && brands.length > 0) {
                new VehicleService()
                    .getModels(
                        cookies.get(Globals.COOKIES.JWT).AccessToken,
                        brands
                    )
                    .then(response => {
                        setInitialFilters({
                            options: {
                                Models: new ParseService().Parse(
                                    response.body.Result
                                ),
                            },
                        });
                    });
            }
        },
        [cookies, setInitialFilters]
    );

    useEffect(() => {
        if (temporaryFilters?.filterBrands?.length) {
            getModels(temporaryFilters.filterBrands);
        }
    }, [temporaryFilters.filterBrands[0]]);

    const printKmFilter = useCallback(() => {
        if (!kmRangeFilterOptions) return; //Validate that min max ranges are set in filters

        return (
            <RangeFilter
                filterTitle={t.getTraduction('Kilometraje')}
                filterCode={filterKm}
                filters={temporaryFilters}
                updateFilters={setTemporaryFilters}
                rangeFilterOptions={kmRangeFilterOptions}
            />
        );
    }, [temporaryFilters, kmRangeFilterOptions, t, setTemporaryFilters]);

    const printMyVehicles = useCallback(() => {
        const myVehiclesButtonGroup = [
            {
                Id: filterFavorite,
                Name: filterFavorite,
                Description: t.getTraduction('Favourits'),
                Value: temporaryFilters.filterFavorite,
            },
            {
                Id: filterBidden,
                Name: filterBidden,
                Description: t.getTraduction('Bids'),
                Value: temporaryFilters.filterBidden,
            },
        ];

        return (
            <ButtonGroupFilter
                filterTitle={t.getTraduction('MyVehicles')}
                filterName="myVehicles"
                filtersInfo={myVehiclesButtonGroup}
                filters={temporaryFilters}
                updateFilters={setTemporaryFilters}
            />
        );
    }, [temporaryFilters, t, setTemporaryFilters]);

    const getListFilter = useCallback(
        (traduction, itemList = [], filterName, isMultiSelect = false) => {
            return (
                <ListFilter
                    filterTitle={traduction}
                    itemList={itemList}
                    filterName={filterName}
                    filters={temporaryFilters}
                    selectFilter={selectFilter}
                    showImages={true}
                    isMultiSelect={isMultiSelect}
                />
            );
        },
        [temporaryFilters, selectFilter]
    );

    const getInputFilter = useCallback(
        (label, filterName) => {
            if (!checkFilterAvailableCall(filterName)) return null;
            return (
                <InputFilter
                    filterTitle={label}
                    filterCode={filterName}
                    filters={temporaryFilters}
                    updateFilters={setTemporaryFilters}
                    containerType={containerType}
                />
            );
        },
        [
            checkFilterAvailableCall,
            containerType,
            temporaryFilters,
            setTemporaryFilters,
        ]
    );

    const optionsDatePurchaseOption = useMemo(
        () => ({
            filterFrom: filterDatePurchaseOptionFrom,
            filterTo: filterDatePurchaseOptionTo,
            filterView: 'default',
            infinite: filtersOptions?.infinite,
            Months: filtersOptions.Months,
        }),
        [filtersOptions.infinite, filtersOptions.Months]
    );

    const optionsDatePurchase = useMemo(
        () => ({
            filterFrom: filterDatePurchaseFrom,
            filterTo: filterDatePurchaseTo,
            filterView: 'default',
            infinite: filtersOptions?.infinite,
            Months: filtersOptions.Months,
        }),
        [filtersOptions.infinite, filtersOptions.Months]
    );

    const applyFilters = useCallback(
        () => refreshFilters(temporaryFilters),
        [refreshFilters, temporaryFilters]
    );

    return (
        <div className="c-vehicle-filters">
            <div className="c-accordion" data-accordion-multiple="true">
                {checkFilterAvailableCall(filterFavorite) && printMyVehicles()}
                {checkFilterAvailableCall(filterAgrupations)
                    ? getListFilter(
                          t.getTraduction('Subastas'),
                          filtersOptions.Agrupations,
                          filterAgrupations,
                          true
                      )
                    : null}
                {checkFilterAvailableCall(filterComercialProducts)
                    ? getListFilter(
                          t.getTraduction('ComercialProducts'),
                          filtersOptions.Products,
                          filterComercialProducts,
                          true
                      )
                    : null}
                {checkFilterAvailableCall(filterFinancialOperation)
                    ? getInputFilter(
                          t.getTraduction('FinancialOperation'),
                          filterFinancialOperation
                      )
                    : null}
                {checkFilterAvailableCall(filterDealers)
                    ? getListFilter(
                          filtersOptions?.labels?.[filterDealers] ||
                              t.getTraduction('ReturnDealers'),
                          filtersOptions.Dealers,
                          filterDealers,
                          true
                      )
                    : null}
                {checkFilterAvailableCall(filterBrands) &&
                    filtersOptions?.Brands?.length &&
                    printBrandFilter()}
                {checkFilterAvailableCall(filterBrands) &&
                checkFilterAvailableCall(filterModels) &&
                filtersOptions?.Models?.length
                    ? getListFilter(
                          t.getTraduction('Modelos'),
                          filtersOptions.Models,
                          filterModels,
                          true
                      )
                    : null}
                {checkFilterAvailableCall(filterKm) && printKmFilter()}
                {checkFilterAvailableCall(filterStates)
                    ? getListFilter(
                          t.getTraduction('States'),
                          filtersOptions.States,
                          filterStates,
                          true
                      )
                    : null}
                {checkFilterAvailableCall(filterBuyer)
                    ? getListFilter(
                          t.getTraduction('Buyers'),
                          filtersOptions.Buyers,
                          filterBuyer,
                          true
                      )
                    : null}
                {checkFilterAvailableCall(filterStadisticsBuyers)
                    ? getListFilter(
                          t.getTraduction('Installations'),
                          filtersOptions.StadisticsBuyers,
                          filterStadisticsBuyers,
                          true
                      )
                    : null}
                {checkFilterAvailableCall(filterTransportState)
                    ? getListFilter(
                          t.getTraduction('Transport'),
                          filtersOptions.Transports,
                          filterTransportState,
                          true
                      )
                    : null}
                {checkFilterAvailableCall(filterPlate)
                    ? getInputFilter(t.getTraduction('Plate'), filterPlate)
                    : null}
                {checkFilterAvailableCall(filterBatch)
                    ? getInputFilter(t.getTraduction('Lote'), filterBatch)
                    : null}

                {checkFilterAvailableCall(filterVIN)
                    ? getInputFilter(t.getTraduction('VIN'), filterVIN)
                    : null}
                {checkFilterAvailableCall(filterDatePurchaseFrom) && (
                    <DatesFilter
                        filterTitle={
                            filtersOptions?.labels?.[filterDatePurchaseFrom] ||
                            t.getTraduction('BuyDate')
                        }
                        filters={temporaryFilters}
                        updateFilters={setTemporaryFilters}
                        options={optionsDatePurchase}
                    />
                )}
                {checkFilterAvailableCall(filterDatePurchaseOptionFrom) && (
                    <DatesFilter
                        filterTitle={
                            filtersOptions?.labels?.[
                                filterDatePurchaseOptionFrom
                            ] || t.getTraduction('PurchaseOptionDate')
                        }
                        filters={temporaryFilters}
                        updateFilters={setTemporaryFilters}
                        options={optionsDatePurchaseOption}
                    />
                )}
                <hr className="u-mb"></hr>
                <Button
                    options={{
                        text: t.getTraduction('ApplyFilters'),
                        name: t.getTraduction('ApplyFilters'),
                        class: 'c-btn--full u-mb',
                    }}
                    onClick={applyFilters}
                />
            </div>
        </div>
    );
};

export default withCookies(VehicleFilters);
